import * as RadixToast from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

type ToastKind = 'default' | 'success' | 'error';
type ToastItem = { id: number; title: string; description?: string; kind: ToastKind };
type ToastContextValue = {
  show: (title: string, opts?: { description?: string; kind?: ToastKind }) => void;
};

const Ctx = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const show = useCallback<ToastContextValue['show']>((title, opts) => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { id, title, description: opts?.description, kind: opts?.kind ?? 'default' }]);
  }, []);
  return (
    <Ctx.Provider value={{ show }}>
      <RadixToast.Provider swipeDirection="right" duration={4000}>
        {children}
        {items.map((t) => (
          <RadixToast.Root
            key={t.id}
            className="mf-card"
            style={{
              padding: 12,
              boxShadow: 'var(--sh-md)',
              borderColor:
                t.kind === 'success' ? 'var(--spark-line)' : t.kind === 'error' ? 'oklch(0.85 0.10 25)' : 'var(--line)',
            }}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((x) => x.id !== t.id));
            }}
          >
            <RadixToast.Title style={{ fontWeight: 550, fontSize: 13 }}>{t.title}</RadixToast.Title>
            {t.description && (
              <RadixToast.Description style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                {t.description}
              </RadixToast.Description>
            )}
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: 320,
            maxWidth: 'calc(100vw - 40px)',
            zIndex: 100,
          }}
        />
      </RadixToast.Provider>
    </Ctx.Provider>
  );
}

export const Toast = RadixToast;
